import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSalesOrderStore } from '../store/salesOrderStore';
import { SalesOrderStatus, type FirmOfferSummaryDto, type ShippingAddressInput, type SubmitSalesOrderRequest } from '../types/salesOrder.types';
import { FirmOfferPicker } from '../components/FirmOfferPicker';
import { FoDetailCard } from '../components/FoDetailCard';
import { FoSummaryCard } from '../components/FoSummaryCard';
import { PerGradeBalanceChips } from '../components/PerGradeBalanceChips';
import { SalesOrderIntakePanel } from '../components/SalesOrderIntakePanel';
import { SalesOrderLineItemsTable } from '../components/SalesOrderLineItemsTable';
import { SalesOrderComposeForm } from '../components/SalesOrderComposeForm';
import { SalesOrderPostingCard } from '../components/SalesOrderPostingCard';
import { SalesOrderPostingStatusCard } from '../components/SalesOrderPostingStatusCard';
import { usePostingSurfacePresence } from '@/features/notifications/hooks/usePostingSurfacePresence';
import { SalesOrderStatusBadge } from '../components/SalesOrderStatusBadge';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { onSalesOrderEvent } from '../hooks/useSalesOrderEventStream';
import {
  fetchOutboxStatus,
  optimisticPostingJob,
  outboxKey,
  outboxType,
  postingBlocksSubmit,
  postingLocksEntity,
  submitBlockedReason,
  type OutboxPostingStatusDto,
} from '@shared/posting';
import { salesOrderService } from '../services/salesOrderService';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';

export function SalesOrderComposePage() {
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId: string }>();
  // N8 review (2026-08-25): this page renders a live posting surface, so it registers presence like
  // the other four — without it, an outcome toast fires on top of the card that just said the same.
  usePostingSurfacePresence('posting.so', draftId && draftId !== 'new' ? draftId : null);

  const activeDraft = useSalesOrderStore((s) => s.activeDraft);
  const isLoadingDraft = useSalesOrderStore((s) => s.isLoadingDraft);
  const loadDraftError = useSalesOrderStore((s) => s.loadDraftError);
  const selectedFirmOffer = useSalesOrderStore((s) => s.selectedFirmOffer);
  const runExtraction = useSalesOrderStore((s) => s.runExtraction);
  const extractingDraftId = useSalesOrderStore((s) => s.extractingDraftId);
  const eligibilityBanner = useSalesOrderStore((s) => s.eligibilityBanner);
  const isSaving = useSalesOrderStore((s) => s.isSaving);
  const isSubmitting = useSalesOrderStore((s) => s.isSubmitting);

  const startNewDraft = useSalesOrderStore((s) => s.startNewDraft);
  const loadDraft = useSalesOrderStore((s) => s.loadDraft);
  const selectFirmOffer = useSalesOrderStore((s) => s.selectFirmOffer);
  const updateDraftFields = useSalesOrderStore((s) => s.updateDraftFields);
  const saveDraft = useSalesOrderStore((s) => s.saveDraft);
  const submitDraft = useSalesOrderStore((s) => s.submitDraft);

  // Local form-only state (lives outside the draft until submit)
  const [shippingAddress, setShippingAddress] = useState<ShippingAddressInput>({
    savedAddressId: null,
    custom: null,
  });

  useEffect(() => {
    if (!draftId || draftId === 'new') {
      startNewDraft();
    } else {
      void loadDraft(Number(draftId));
    }
  }, [draftId, startNewDraft, loadDraft]);

  // Sync local shippingAddress with the loaded draft
  useEffect(() => {
    if (activeDraft?.shippingAddressId) {
      setShippingAddress({ savedAddressId: activeDraft.shippingAddressId, custom: null });
    }
  }, [activeDraft?.shippingAddressId]);

  // 86d3mnuxe FIX 6 — this page previously had ZERO outbox state: during an in-flight post the draft
  // still reads "Draft" (posting state lives on the outbox), so the page kept Save/Submit live for
  // actions the BE mutation guard now rejects. Fetch the projection on the loaded draft + refetch on
  // an SSE posting nudge (same shape as SalesOrderDetailPanel), and lock the page through it.
  const activeServerId = activeDraft && activeDraft.id > 0 ? activeDraft.id : null;
  const [posting, setPosting] = useState<OutboxPostingStatusDto | null>(null);
  useEffect(() => {
    if (activeServerId === null) {
      setPosting(null);
      return;
    }
    let cancelled = false;
    const load = () =>
      fetchOutboxStatus(outboxKey.salesOrder(activeServerId))
        .then((jobs) => {
          // Prefer an in-flight Posting job over a stale Failed sibling; else the most-recent job.
          if (!cancelled)
            setPosting(jobs.find((j) => j.status === 'Posting') ?? jobs[jobs.length - 1] ?? null);
        })
        .catch(() => {
          if (!cancelled) setPosting(null);
        });
    void load();
    const off = onSalesOrderEvent((evt) => {
      if (evt.salesOrderId === activeServerId && evt.postingStatus != null) void load();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [activeServerId]);

  // 86d3mnuxe funnel — resolve a Failed posting intent. Submitter-level (anyone who can submit can
  // cancel their NotReached failure; the BE refuses a reached cancel).
  const canResolvePosting = useAuthStore(
    (s) =>
      s.hasPermission(PERMISSIONS.SALES_ORDERS_SUBMIT_SALES_ORDER) ||
      s.hasPermission(PERMISSIONS.SYSTEM_MANAGE_OUTBOX),
  );
  const handleCancelPost = async () => {
    if (activeServerId === null) return;
    try {
      await salesOrderService.cancelPosting(activeServerId);
      setPosting(null); // Cancelled drops out of the projection — null is the fresh truth
      toast.success('Posting cancelled', {
        description: 'The failed post is closed — you can keep editing and submit again.',
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to cancel the post');
    }
  };

  // 86d3mnuxe funnel — Reconcile a REACHED failure: the BE re-arms the same job (frozen decision) so
  // the adopt records the Sales Order NetSuite already accepted. No re-decision, no prompt.
  const handleReconcilePost = async () => {
    if (activeServerId === null) return;
    try {
      await salesOrderService.reconcilePosting(activeServerId);
      setPosting(optimisticPostingJob(outboxKey.salesOrder(activeServerId), outboxType.salesOrder));
      toast.info('Reconciling — recovering the record NetSuite already accepted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to start the reconcile');
    }
  };

  const isSubmitted = activeDraft?.status === SalesOrderStatus.Submitted;
  // Locked while a post is in flight / after a reached failure (mirrors the BE mutation guard;
  // a NotReached failure stays editable — editing is the fix).
  const isReadOnly = isSubmitted || postingLocksEntity(posting);

  const handleFoPick = async (fo: FirmOfferSummaryDto) => {
    if (activeDraft && activeDraft.lineItems.length > 0) {
      if (!confirm('Changing FO will clear extracted line items. Continue?')) return;
      updateDraftFields({ lineItems: [] });
    }
    await selectFirmOffer(fo);
  };

  const handleSave = async () => {
    const saved = await saveDraft();
    if (saved && (!draftId || draftId === 'new')) {
      navigate(`/sales-orders/compose/${saved.id}`, { replace: true });
    }
  };

  const handleSubmit = async () => {
    if (!activeDraft || !activeDraft.linkedFirmOfferInternalId) {
      toast.error('Pick a Firm Offer first');
      return;
    }
    // 86d3mnuxe funnel — reached failures never route here any more: Submit is disabled while a
    // Failed job exists, and the card's Reconcile re-arms the frozen job server-side. This path is
    // always a genuine (re-)submission — persist first so a server id exists.
    const saved = await saveDraft();
    if (!saved) return;

    const request: SubmitSalesOrderRequest = {
      salesOrderId: saved.id,
      linkedFirmOfferInternalId: saved.linkedFirmOfferInternalId!,
      items: saved.lineItems.map((li) => ({
        matchedItemId: li.matchedItemId ?? '',
        quantity: li.quantity ?? 0,
        itemCode: li.matchedItemId,
        gradeCode: li.gradeCode,
        priceLevel: li.priceLevel,
      })),
      customerPoNumber: saved.customerPoNumber,
      poExpiryDate: saved.poExpiryDate,
      deliveryDueDate: saved.deliveryDueDate,
      stateProvinceId: saved.shippingStateProvinceId ?? '',
      cityTownId: saved.shippingCityTownId ?? '',
      waivedFreight: saved.waivedFreight,
      separateBilling: saved.separateBilling,
      includeFreightLine: saved.includeFreightLine,
      shippingAddress,
      doCustCompanyId: saved.doCustCompanyId,
      doCustCompanyName: saved.doCustCompanyName,
      billingAddress: saved.billingAddressId
        ? { savedAddressId: saved.billingAddressId, custom: null }
        : null,
      termsId: saved.termsId,
      termsName: saved.termsName,
      projectName: saved.projectName,
      alertInvoiceECopy: saved.alertInvoiceECopy,
      alertManualComputation: saved.alertManualComputation,
      alertMc2Copies: saved.alertMc2Copies,
      alertDrPicture: saved.alertDrPicture,
      alertDrAttachment: saved.alertDrAttachment,
      alertBrnTraceability: saved.alertBrnTraceability,
      scheduleId: saved.scheduleId,
      authorizedReceivers: saved.authorizedReceivers,
      otherInstructions: saved.otherInstructions,
      soRemarks: saved.soRemarks,
      subPoOrderlistLink: saved.subPoOrderlistLink,
      invoiceToLetter: saved.invoiceToLetter,
      officeContact1: saved.officeContact1,
      contactNumber1: saved.contactNumber1,
      officeContact2: saved.officeContact2,
      contactNumber2: saved.contactNumber2,
      officeContact3: saved.officeContact3,
      contactNumber3: saved.contactNumber3,
      isTrailer: saved.isTrailer,
      isMf: saved.isMf,
      isPpeRequired: saved.isPpeRequired,
      isPermitRequired: saved.isPermitRequired,
      isPriority: saved.isPriority,
      isSpotOrder: saved.isSpotOrder,
      isReprint: saved.isReprint,
      waitingForOfficialPo: saved.waitingForOfficialPo,
      freightChargePerTon: saved.freightChargePerTon, // 86d3fteqa: editable rate drives the charge (legacy path)
      actualFreightPerMt: saved.actualFreightPerMt, // informational only
      isDiscountedFreight: saved.isDiscountedFreight,
      discountDetails: saved.discountDetails,
      reasonForClosing: saved.reasonForClosing,
      subagentId: saved.subagentId,
      date: saved.date,
    };
    const claimed = await submitDraft(request);
    // 86d3peur0 #12 — engage the read-only lock + posting card NOW (the claim committed the job;
    // `posting` only refreshes on the SSE nudge). Gated on the return value: submitDraft swallows
    // failures (toasts + returns false), so an unconditional flip would show "posting…" for a
    // submit that never started.
    if (claimed) {
      setPosting(optimisticPostingJob(outboxKey.salesOrder(request.salesOrderId), outboxType.salesOrder));
    }
  };

  if (isLoadingDraft) {
    return (
      <div className="space-y-4 p-6">
        <PanelSkeleton rows={4} />
        <PanelSkeleton rows={6} />
      </div>
    );
  }

  if (loadDraftError) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/sales-orders')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to list
        </Button>
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          {loadDraftError}
        </div>
      </div>
    );
  }

  if (!activeDraft) return null;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sales-orders')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">
                {activeDraft.salesOrderNumber || 'New Sales Order'}
              </h1>
              {activeDraft.id > 0 && <SalesOrderStatusBadge status={activeDraft.status} />}
            </div>
            {activeDraft.linkedFirmOfferTranId && (
              <p className="text-sm text-muted-foreground">
                Drawn from {activeDraft.linkedFirmOfferTranId}
              </p>
            )}
          </div>
        </div>
        {!isReadOnly && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Draft
            </Button>
            <Button
              onClick={handleSubmit}
              // 86d3mnuxe funnel — one posting intent at a time: while a job is in flight or Failed,
              // submit is disabled and the posting card's Retry/Cancel/Reconcile is the way forward.
              disabled={isSubmitting || !activeDraft.linkedFirmOfferInternalId || postingBlocksSubmit(posting)}
              title={submitBlockedReason(posting)}
            >
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Submit to NetSuite
            </Button>
          </div>
        )}
      </div>

      {eligibilityBanner && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>{eligibilityBanner}</div>
        </div>
      )}

      {/* 86d3mnuxe — posting-lifecycle card driven by the OUTBOX status (Posting spinner / Failed
          retry-or-reconcile / reach-gated admin Cancel). Suppressed once actually Submitted — the
          entity card below owns the posted-success summary. */}
      {posting && !isSubmitted && (
        <SalesOrderPostingStatusCard
          posting={posting}
          // 86d3mnuxe funnel — NotReached Retry = cancel the failed intent, then re-submit what's on
          // screen (re-decides; the drift 409 can surface). Reconcile re-arms the frozen job.
          onRetry={() => {
            void (async () => {
              if (activeServerId === null) return;
              try {
                await salesOrderService.cancelPosting(activeServerId);
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Unable to retry the post');
                return;
              }
              await handleSubmit();
            })();
          }}
          onReconcile={() => void handleReconcilePost()}
          onCancel={canResolvePosting ? () => void handleCancelPost() : null}
          isBusy={isSubmitting}
        />
      )}

      {isSubmitted && <SalesOrderPostingCard salesOrder={activeDraft} />}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Firm Offer</CardTitle>
        </CardHeader>
        <CardContent>
          <FirmOfferPicker
            selectedTranId={activeDraft.linkedFirmOfferTranId}
            onSelect={handleFoPick}
            disabled={isReadOnly}
          />
        </CardContent>
      </Card>

      <FoDetailCard />

      {selectedFirmOffer && (
        <>
          <FoSummaryCard firmOffer={selectedFirmOffer} />
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Per-grade Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <PerGradeBalanceChips
                firmOffer={selectedFirmOffer}
                lineItems={activeDraft.lineItems}
              />
            </CardContent>
          </Card>

          {!isReadOnly && (
            <SalesOrderIntakePanel
              onExtract={(source) => void runExtraction(source)}
              isExtracting={extractingDraftId != null && extractingDraftId === activeDraft.id}
            />
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <SalesOrderLineItemsTable
                lineItems={activeDraft.lineItems}
                onChange={(lineItems) => updateDraftFields({ lineItems })}
                readOnly={isReadOnly}
              />
            </CardContent>
          </Card>

          <SalesOrderComposeForm
            draft={activeDraft}
            shippingAddress={shippingAddress}
            onUpdateDraft={updateDraftFields}
            onShippingAddressChange={setShippingAddress}
            readOnly={isReadOnly}
          />
        </>
      )}
    </div>
  );
}
